import EnhancedTextField from "../../../../../../../components/EnhancedTextField";
import EnhancedSelect from "../../../../../../../components/EnhancedSelect";
import React, {useEffect} from "react";
import {createAttribute as utilCreateAttribute, getAttributes as utilGetAttributes, getAttributeSets as utilGetAttributesSets, updateAttribute as utilUpdateAttribute, updateAttributeSet as utilUpdateAttributeSet} from "../../../../../../../utils/utils";
import EnhancedAutocomplete from "../../../../../../../components/EnhancedAutocomplete";
import SettingsDialog from "../SettingsDialog";
import {Accordion, AccordionDetails, AccordionSummary, Button, Grid, IconButton, InputLabel, List, ListItem, ListItemSecondaryAction, ListItemText, Typography} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Clear} from "@mui/icons-material";
import {List as ListLoader} from "react-content-loader";
import Auth from "../../../../../../../auth";
import RuleConditonalInput from "./components/RuleConditonalInput";
import CatalogCheckbox from "../../../../../../../components/EnhancedCheckbox";
import { pageContentGap } from '../../../../../../../theme';

interface ErrorState {
    open?: boolean;
    errorHeader?: string;
    errorMessage?: string;
    [key: string]: any;
}

interface RuleType {
    value: string;
    label: string;
    parameter?: string;
}

interface Attribute {
    Name?: string;
    Label?: string;
    FriendlyName?: string;
    Type?: string;
    Options?: any[];
    Editor?: string;
    AttributeSet?: string;
    Group?: string;
    Description?: string;
    DestinationId?: string;
    Hidden?: boolean;
    HiddenFromPartner?: boolean;
    System?: boolean;
    ValidationRules?: any[];
    [key: string]: any;
}

interface AttributeSet {
    Name?: string;
    Attributes?: string[];
    [key: string]: any;
}

// Multi-select option describing an attribute set the attribute is in / was removed from.
interface AttributeSetOption {
    value: string;
    [key: string]: any;
}

interface ComputeAttributeSetUpdatesArgs {
    availableAttributeSets?: AttributeSet[];
    includedInSets?: AttributeSetOption[];
    removedFromSets?: AttributeSetOption[];
    oldName?: string;
    newName?: string;
}

interface AttributeDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    isEdit?: boolean;
    previousAttribute?: Attribute | null;
    setConfig: (updater: (prevState: any) => any) => void;
    outsideEditing?: boolean;
    onComplete?: (newAttribute: Attribute, runStatus: any) => void;
    config?: any;
    history?: any;
    location?: any;
}

const collapsableTabSx = {
    borderRadius: "6px",
    marginBottom: "12px",
    "&:before": {
        display: "none"
    }
};

const defaultAttribute: Attribute = {
    Name: "",
    Label: "",
    Type: "",
    Options: [],
    Editor: "",
    AttributeSet: "",
    Group: "",
    Description: "",
    Hidden: false,
    HiddenFromPartner: false,
}

const ruleTypes: RuleType[] = [
    {value: 'required', label: 'Required'},
    {value: 'optional', label: 'Optional'},
    {value: 'equal', label: 'Equals', parameter: 'value'},
    {value: 'notequal', label: 'Not Equals', parameter: 'value'},
    {value: 'containedin', label: 'In List', parameter: 'value'},
    {value: 'match', label: 'Matches Expression', parameter: 'regex'},
    {value: 'lengtheq', label: 'Length Equals', parameter: 'value'},
    {value: 'lengthlt', label: 'Length Less Than', parameter: 'value'}
];

// Reconcile attribute-set membership for the attribute being saved.
// Attribute sets reference their members by internal Name, so on a rename
// (oldName !== newName) the stale old name must be dropped from every affected
// set before the new name is added — otherwise an orphaned reference to a
// now-nonexistent attribute is left behind. Returns the sets that need to be
// persisted, each with a corrected (non-mutated) Attributes list.
export const computeAttributeSetUpdates = ({
    availableAttributeSets = [],
    includedInSets = [],
    removedFromSets = [],
    oldName,
    newName,
}: ComputeAttributeSetUpdatesArgs): AttributeSet[] => {
    const isRename = !!oldName && oldName !== newName;
    const findSet = (value: string) => availableAttributeSets.find((s) => s.Name === value);
    const updates: AttributeSet[] = [];

    includedInSets.forEach((set) => {
        const attributeSet = findSet(set.value);
        if (!attributeSet) return;

        let attributes = attributeSet.Attributes ? [...attributeSet.Attributes] : [];
        if (isRename) attributes = attributes.filter((a) => a !== oldName);
        if (newName !== undefined && !attributes.includes(newName)) attributes.push(newName);

        updates.push({...attributeSet, Attributes: attributes});
    });

    const stillIncluded = new Set(includedInSets.map((set) => set.value));

    removedFromSets.forEach((set) => {
        // A set that was removed and then re-added in the same session appears in both lists.
        // Membership wins, otherwise this loop emits a second, conflicting update for a set the
        // includedInSets pass has already handled.
        if (stillIncluded.has(set.value)) return;

        const attributeSet = findSet(set.value);
        if (!attributeSet) return;

        const attributes = (attributeSet.Attributes || []).filter((a) => a !== newName && a !== oldName);
        updates.push({...attributeSet, Attributes: attributes});
    });

    return updates;
};

export default function AttributeDialog(props: AttributeDialogProps) {
    const {open, setOpen, isEdit, previousAttribute} = props;
    const hasPermission = Auth.hasPermission("settings/manage");

    const [addToSets, setAddToSets] = React.useState<any[]>([]);
    const [removedFromSets, setRemovedFromSets] = React.useState<any[]>([]);
    const [includedInSets, setIncludedInSets] = React.useState<any[]>([]);
    const [initIncludedInSets, setInitIncludedInSets] = React.useState<any[]>([]);

    const [availableAttributeSets, setAvailableAttributeSets] = React.useState<any[]>([]);
    const [availableGroups, setAvailableGroups] = React.useState<any[]>([]);

    const [newAttribute, setNewAttribute] = React.useState<Attribute>(defaultAttribute);
    const [initAttribute, setInitAttribute] = React.useState<Attribute>(defaultAttribute);
    // The name the attribute currently has on the server, which is what identifies it in a PUT.
    // previousAttribute is a prop and the state-reseeding effect only runs when [open] changes,
    // so saving twice without closing the dialog would keep re-PUTting against the original name.
    const [attributeId, setAttributeId] = React.useState<string | undefined>(undefined);
    const [validationErrors, setValidationErrors] = React.useState<any>({});
    const [error, setError] = React.useState<ErrorState>({});
    const [loading, setLoading] = React.useState(false);
    const [waiting, setWaiting] = React.useState(true);

    const validate = () => {
        if (!hasPermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            });
            return false;
        }

        let newValidationErrors: any = {};
        if (!newAttribute.Name || newAttribute.Name === "") {
            newValidationErrors.Name = "Name is required";
        }
        if (!newAttribute.FriendlyName || newAttribute.FriendlyName === "") {
            newValidationErrors.FriendlyName = "Label is required";
        }
        if (!newAttribute.Type || newAttribute.Type === "") {
            newValidationErrors.Type = "Type is required";
        }
        if (!newAttribute.Group || newAttribute.Group === "") {
            newValidationErrors.Group = "Group is required";
        }
        if (newAttribute.ValidationRules && newAttribute.ValidationRules.length > 0) {
            newAttribute.ValidationRules.forEach((rule: any, index: number) => {
                rule.Conditions && rule.Conditions.forEach((condition: any, conditionIndex: number) => {
                    if (condition.Field === "" || condition.Field === undefined) {
                        let ruleErrors = newValidationErrors.ValidationRules || [];
                        ruleErrors[index] = ruleErrors[index] || {};
                        ruleErrors[index].Conditions = ruleErrors[index].Conditions || [];
                        ruleErrors[index].Conditions[conditionIndex] = ruleErrors[index].Conditions[conditionIndex] || {};
                        ruleErrors[index].Conditions[conditionIndex].Field = "Field is required";
                        newValidationErrors.ValidationRules = ruleErrors;
                    }
                })
            });
        }

        setValidationErrors(newValidationErrors);
        return Object.keys(newValidationErrors).length <= 0;
    }

    const createAttribute = async () => {
        let validateStatus = validate();
        if (!validateStatus) return;
        setLoading(true);
        let runStatus = await utilCreateAttribute(newAttribute);
        if (runStatus !== true) setError(runStatus);
        else {
            // Creating over a set member that no longer exists still has a stale name to clear.
            await updateAttributeSets(previousAttribute ? previousAttribute.Name : undefined);
            await new Promise(resolve => setTimeout(resolve, 750));
            props.setConfig(prevState => ({...prevState, refresh: !prevState.refresh}))
        }

        if (props.outsideEditing) setOpen(false);

        setOpen(false);
        setLoading(false);
        setInitAttribute(newAttribute);
        setAddToSets([]);
        setInitIncludedInSets(includedInSets);
        if (props.onComplete) props.onComplete(newAttribute, runStatus);
    }

    const updateAttribute = async () => {
        let validateStatus = validate();
        if (!validateStatus) return;
        setLoading(true);
        const currentId = attributeId ?? previousAttribute?.Name ?? newAttribute.Name;
        let runStatus = await utilUpdateAttribute(newAttribute, currentId);
        if (runStatus !== true) setError(runStatus);
        else {
            // The rename has landed, so subsequent saves must target the new name.
            setAttributeId(newAttribute.Name);

            if(!newAttribute.System) {
                await updateAttributeSets(currentId);
            }

            await new Promise(resolve => setTimeout(resolve, 750));
            props.setConfig(prevState => ({...prevState, refresh: !prevState.refresh}))
        }
        setLoading(false);
        setInitAttribute(newAttribute);
        setAddToSets([]);
        setInitIncludedInSets(includedInSets);
        if (props.onComplete) props.onComplete(newAttribute, runStatus);
    }

    const updateAttributeSets = async (oldName?: string) => {
        // Callers pass the name the attribute had on the server before this save, so a renamed
        // attribute's stale entries are cleaned up.
        const updates = computeAttributeSetUpdates({
            availableAttributeSets,
            includedInSets,
            removedFromSets,
            oldName,
            newName: newAttribute.Name,
        });

        await Promise.all(updates.map(async (attributeSet) => {
            let runStatus = await utilUpdateAttributeSet(attributeSet, attributeSet.Name);
            if (runStatus && runStatus.errorMessage) setError(runStatus);
        }));
    }

    const addValidationRule = () => {
        setNewAttribute((prevState) => ({
            ...prevState,
            ValidationRules: [...(prevState.ValidationRules !== undefined ? prevState.ValidationRules : []), {
                Note: "",
                Type: "optional",
                ErrorMessage: "",
                Parameters: {
                    value: ""
                }
            }]
        }));
    }

    const removeValidationRule = (index: number) => {
        if (index === 0 && newAttribute.ValidationRules!.length === 1) {
            setNewAttribute((prevState) => ({
                ...prevState,
                ValidationRules: undefined
            }));

            return;
        }

        setNewAttribute((prevState) => ({
            ...prevState,
            ValidationRules: prevState.ValidationRules!.filter((_: any, i: number) => i !== index)
        }));
    }

    const addCondition = (index: number) => {
        setNewAttribute((prevState) => ({
            ...prevState,
            ValidationRules: prevState.ValidationRules!.map((r: any, i: number) => i === index ? {
                ...r,
                Conditions: [...(r.Conditions !== undefined ? r.Conditions : []), {
                    Field: "",
                    Type: "optional",
                    Parameters: {
                        value: ""
                    }
                }]
            } : r)
        }));
    }

    const removeCondition = (index: number, conditionIndex: number) => {
        if (conditionIndex === 0 && newAttribute.ValidationRules![index].Conditions.length === 1) {
            setNewAttribute((prevState) => ({
                ...prevState,
                ValidationRules: prevState.ValidationRules!.map((r: any, i: number) => i === index ? {
                    ...r,
                    Conditions: undefined
                } : r)
            }));

            return;
        }

        setNewAttribute((prevState) => ({
            ...prevState,
            ValidationRules: prevState.ValidationRules!.map((r: any, i: number) => i === index ? {
                ...r,
                Conditions: r.Conditions.filter((_: any, i: number) => i !== conditionIndex)
            } : r)
        }));
    }

    const updateValidationRule = (index: number, rule: any) => {
        setNewAttribute((prevState) => ({
            ...prevState,
            ValidationRules: prevState.ValidationRules!.map((r: any, i: number) => i === index ? rule : r)
        }));
    }

    const updateValidationRuleCondition = (index: number, conditionIndex: number, condition: any) => {
        setNewAttribute((prevState) => ({
            ...prevState,
            ValidationRules: prevState.ValidationRules!.map((r: any, i: number) => i === index ? {
                ...r,
                Conditions: r.Conditions.map((c: any, i: number) => i === conditionIndex ? condition : c)
            } : r)
        }));
    }

    useEffect(() => {
        setIncludedInSets([]);
        setInitIncludedInSets([]);
        setRemovedFromSets([]);
        setAddToSets([]);

        setAttributeId(previousAttribute ? previousAttribute.Name : undefined);

        if (previousAttribute) {
            setNewAttribute(previousAttribute);
            setInitAttribute(previousAttribute);
        } else {
            setNewAttribute(defaultAttribute);
            setInitAttribute(defaultAttribute);
        }

        const getAttributeSets = async () => {
            let attributeSets = await utilGetAttributesSets(undefined, undefined, undefined, true);
            if (attributeSets.errorMessage) setError(attributeSets);
            else setAvailableAttributeSets(attributeSets);
        }

        const getAvailableGroups = async () => {
            let groups = await utilGetAttributes(undefined, true);
            if (groups.errorMessage) setError(groups);
            else setAvailableGroups(groups.map((g: any) => g.Group).filter((g: any, i: number, a: any[]) => a.indexOf(g) === i));
        }

        const loadData = async () => {
            setWaiting(true);
            const promise1 = getAttributeSets();
            const promise2 = getAvailableGroups();
            await Promise.all([promise1, promise2])
                .then(() => setWaiting(false));
        }

        loadData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        let includedInSets = availableAttributeSets
            .filter((set) => set.Attributes.includes(newAttribute.Name))
            .map((set) => ({
                label: set.FriendlyName,
                value: set.Name
            }));
        setIncludedInSets(includedInSets)
        setInitIncludedInSets(includedInSets);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableAttributeSets]);

    return (
        <SettingsDialog open={open} setOpen={setOpen} isEdit={isEdit} noPrefix={newAttribute.System || !hasPermission}
                        disabled={!hasPermission}
                        label={(newAttribute.System || !hasPermission) ? "View Attribute" : "Attribute"}
                        subtitle={
                            newAttribute.System
                                ? "Only validation rules can be changed on system attributes."
                                : !hasPermission
                                    ? "You do not have permission to edit this attribute."
                                    : ""
                        }
                        error={error} setError={setError}
                        newEntity={{...newAttribute, addToSets: addToSets, includedInSets: includedInSets}}
                        initEntity={{...initAttribute, addToSets: addToSets, includedInSets: initIncludedInSets}}
                        loading={loading} loadingHeader={isEdit ? "Updating Attribute" : "Creating Attribute"}
                        loadingMessage={"Please wait..."}
                        update={updateAttribute} create={createAttribute}

                        contentStyles={{
                            width: "800px"
                        }}

                        inputs={
                            <>
                                <EnhancedTextField disabled={newAttribute.System || !hasPermission} label={"Name"}
                                                   required
                                                   inputProps={{"data-testid": "poc-attribute-name-input"}}
                                                   value={newAttribute.Name}
                                                   error={validationErrors.Name} helperText={validationErrors.Name}
                                                   onChange={(e: any) => setNewAttribute({
                                                       ...newAttribute,
                                                       Name: e.target.value
                                                   })}
                                                   style={{width: "100%"}}/>

                                <EnhancedTextField disabled={newAttribute.System || !hasPermission}
                                                   label={"Friendly Name"} required
                                                   inputProps={{"data-testid": "poc-attribute-friendly-name-input"}}
                                                   value={newAttribute.FriendlyName}
                                                   error={validationErrors.FriendlyName}
                                                   helperText={validationErrors.FriendlyName}
                                                   onChange={(e: any) => setNewAttribute({
                                                       ...newAttribute,
                                                       FriendlyName: e.target.value
                                                   })}
                                                   style={{width: "100%"}}/>

                                <Grid data-testid="poc-attribute-type-select" style={{width: "100%"}}>
                                <EnhancedSelect disabled={newAttribute.System || !hasPermission} label={"Type"} required
                                                isClearable
                                                error={validationErrors.Type} helperText={validationErrors.Type}
                                                value={{label: newAttribute.Type, value: newAttribute.Type}}
                                                onChange={(e: any) => setNewAttribute({...newAttribute, Type: e ? e.value : ''})}
                                                options={[
                                                    {label: "text", value: "text"},
                                                    {label: "select", value: "select"},
                                                    {label: "number", value: "number"},
                                                    {label: "image", value: "image"},
                                                    {label: "yesno", value: "yesno"},
                                                    {label: "list", value: "list"},
                                                    {label: "video", value: "video"},
                                                    {label: "document", value: "document"},
                                                ]}
                                                style={{width: "100%"}}/>
                                </Grid>

                                {newAttribute.Type === "select" && (
                                    <EnhancedAutocomplete disabled={newAttribute.System || !hasPermission}
                                                          label={"Options"}
                                                          options={[]}
                                                          value={newAttribute.Options}
                                                          onChange={(e: any, value: any) => {
                                                              setNewAttribute({
                                                                  ...newAttribute,
                                                                  Options: value
                                                              })
                                                          }}
                                    />
                                )}

                                {newAttribute.Type === "text" && (
                                    <EnhancedSelect disabled={newAttribute.System || !hasPermission}
                                                    label={"Editor Type"}
                                                    isClearable={true}
                                                    value={{
                                                        label: newAttribute.Editor,
                                                        value: newAttribute.Editor
                                                    }}
                                                    onChange={(e: any) => setNewAttribute({
                                                        ...newAttribute,
                                                        Editor: e === null ? "" : e.value
                                                    })}
                                                    options={[{
                                                        label: "textarea",
                                                        value: "textarea"
                                                    }, {
                                                        label: "html",
                                                        value: "html"
                                                    }]}
                                                    style={{width: "100%"}} $selectorStyles={{height: "100%"}}/>
                                )}

                                <Grid data-testid="poc-attribute-display-group-autocomplete" style={{width: "100%"}}>
                                <EnhancedAutocomplete disabled={newAttribute.System || !hasPermission}
                                                      label={"Display Group"}
                                                      required
                                                      notMultiple={true} options={availableGroups}
                                                      value={newAttribute.Group || null}
                                                      error={validationErrors.Group}
                                                      helperText={
                                                          <>
                                                              Type a new group name or select one from the list.
                                                              <br/>
                                                              {validationErrors.Group}
                                                          </>
                                                      }
                                                      onChange={(e: any, value: any) => {
                                                          setNewAttribute({
                                                              ...newAttribute,
                                                              Group: value
                                                          })
                                                      }}
                                                      style={{width: "100%"}}/>
                                </Grid>

                                <EnhancedTextField
                                    disabled={newAttribute.System || !hasPermission}
                                    label={"Destination ID"}
                                    value={newAttribute.DestinationId}
                                    onChange={(e: any) => setNewAttribute({
                                        ...newAttribute,
                                        DestinationId: e.target.value
                                    })}
                                    style={{width: "100%"}}/>

                                <Grid container alignItems={"center"}>
                                    <Typography style={{fontSize: "16px", color: "rgba(0, 0, 0, 0.54)"}}>
                                        Hidden:
                                    </Typography>
                                    <CatalogCheckbox
                                        disabled={newAttribute.System || !hasPermission}
                                        label={"Hidden"}
                                        checked={newAttribute.Hidden}
                                        onChange={(e: any) => setNewAttribute({
                                            ...newAttribute,
                                            Hidden: e.target.checked
                                        })}
                                        style={{width: "100%"}}/>
                                </Grid>

                                <Grid container alignItems={"center"}>
                                    <Typography style={{fontSize: "16px", color: "rgba(0, 0, 0, 0.54)"}}>
                                        Hidden From Partner:
                                    </Typography>
                                    <CatalogCheckbox
                                        disabled={newAttribute.System || !hasPermission}
                                        label={"Hidden From Partner"}
                                        checked={newAttribute.HiddenFromPartner}
                                        onChange={(e: any) => setNewAttribute({
                                            ...newAttribute,
                                            HiddenFromPartner: e.target.checked
                                        })}
                                        style={{width: "100%"}}/>
                                </Grid>

                                {/*Attribute dialog can be opened from the attributeSet editor*/}
                                {!props.outsideEditing && (
                                    <Grid container style={{gap: pageContentGap, flexDirection: "column"}}>
                                        {!newAttribute.System && (
                                            <Grid container justifyContent={"center"} alignItems={"flex-end"}
                                                  style={{gap: pageContentGap}}>
                                                <Grid data-testid="poc-attribute-add-to-set-select" style={{flex: 0.8}}>
                                                <EnhancedSelect
                                                    disabled={newAttribute.System || waiting || !hasPermission}
                                                    label={"Add to Attribute Set"}
                                                    isMulti={true} loading={waiting}
                                                    isClearable
                                                    value={addToSets.map((set) => ({
                                                        label: set.label,
                                                        value: set.value,
                                                    }))}
                                                    onChange={(e: any) => {
                                                        setAddToSets(e || []);
                                                    }}
                                                    options={
                                                        availableAttributeSets.filter((set) => !includedInSets.map(set => set.value).includes(set.Name))
                                                            .map(set => {
                                                                return {
                                                                    label: set.FriendlyName,
                                                                    value: set.Name,
                                                                    disabled: set.System
                                                                }
                                                            })
                                                    }
                                                    style={{width: "100%"}} $selectorStyles={{height: "100%"}}
                                                />
                                                </Grid>

                                                <Button variant="contained" color="primary"
                                                    data-testid="poc-attribute-add-to-set-button"
                                                    style={{flex: 0.2}}
                                                    disabled={newAttribute.System || waiting || !hasPermission}
                                                    onClick={() => {
                                                        const readded = addToSets.map((set) => set.value);
                                                        setIncludedInSets([...includedInSets, ...addToSets]);
                                                        // Re-adding a set undoes its earlier removal; leaving it queued for
                                                        // removal would send two conflicting PUTs for the same set on save.
                                                        setRemovedFromSets(removedFromSets.filter((set) => !readded.includes(set.value)));
                                                        setAddToSets([]);
                                                    }}
                                                >
                                                    Add
                                                </Button>
                                            </Grid>
                                        )}

                                        <Grid container>
                                            <InputLabel style={{color: "rgba(0, 0, 0, 0.54)"}}>
                                                Included In Attribute Sets ({includedInSets.length})
                                            </InputLabel>

                                            {waiting ? (
                                                <ListLoader preserveAspectRatio='none'
                                                            style={{height: '150px', width: '100%', margin: "10px 0"}}
                                                            primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                                            ) : (
                                                <List dense={true}
                                                      style={{width: "100%", maxHeight: "200px", overflow: "auto"}}>
                                                    {includedInSets.map((set) => {
                                                        const attributeSetName = set.label || availableAttributeSets.find((s) => s.Name === set).FriendlyName;
                                                        return (
                                                            <ListItem>
                                                                <ListItemText
                                                                    primary={attributeSetName}
                                                                />
                                                                {(!newAttribute.System && hasPermission) && (
                                                                    <ListItemSecondaryAction>
                                                                        <IconButton
                                                                            edge="end"
                                                                            aria-label="delete"
                                                                            onClick={() => {
                                                                                setIncludedInSets(includedInSets.filter((s) => s.value !== set.value));
                                                                                setRemovedFromSets([...removedFromSets, set]);
                                                                            }}
                                                                            size="large">
                                                                            <Clear/>
                                                                        </IconButton>
                                                                    </ListItemSecondaryAction>
                                                                )}
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            )}
                                        </Grid>
                                    </Grid>
                                )}

                                <EnhancedTextField disabled={newAttribute.System || !hasPermission}
                                                   label={"Description"}
                                                   value={newAttribute.Description}
                                                   onChange={(e: any) => setNewAttribute({
                                                       ...newAttribute,
                                                       Description: e.target.value
                                                   })}
                                                   style={{width: "100%"}} multiline/>

                                {(newAttribute.ValidationRules && newAttribute.ValidationRules.length > 0) && (
                                    <Grid>
                                        <Typography
                                            style={{fontSize: "16px"}}>
                                            Validation Rules
                                            ({newAttribute.ValidationRules.length})
                                        </Typography>

                                        {newAttribute.ValidationRules.map((rule: any, index: number) => {
                                            let ruleType = ruleTypes.find((r) => r.value === rule.Type)!;
                                            let hasParameter = ruleType !== undefined && ruleType.parameter !== undefined;
                                            return (
                                                <Accordion
                                                    defaultExpanded={index === (newAttribute.ValidationRules!.length - 1) ? true : false}
                                                    elevation={0}
                                                    sx={collapsableTabSx}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon/>}
                                                        aria-controls="panel1a-content"
                                                        id="panel1a-header"
                                                    >
                                                        <Grid container style={{flexDirection: "column"}}>
                                                            <Typography style={{fontSize: "12px", fontWeight: 300}}>
                                                                <React.Fragment key={index}>
                                                                    {newAttribute.FriendlyName}
                                                                    {(ruleType.label === "Required" || ruleType.label === "Optional") ? " is" : " "} {ruleType.label} {" "}
                                                                    {hasParameter && rule.Parameters.value}
                                                                    <br/>
                                                                </React.Fragment>
                                                            </Typography>
                                                        </Grid>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Grid container>
                                                            <Grid item container key={index} style={{
                                                                border: "solid 1px black",
                                                                borderRadius: "6px",

                                                                padding: "10px",
                                                                marginBottom: "12px",

                                                                flexDirection: "column",
                                                                width: "100%",
                                                                gap: "10px"
                                                            }}>
                                                                <EnhancedSelect label={"Rule Type"}
                                                                                disabled={!hasPermission}
                                                                                value={{
                                                                                    label: ruleTypes.find((r) => r.value === rule.Type)!.label,
                                                                                    value: rule.Type
                                                                                }}
                                                                                onChange={(e: any) => updateValidationRule(index, {
                                                                                    ...rule,
                                                                                    Type: e.value
                                                                                })}
                                                                                options={ruleTypes.map((r) => ({
                                                                                    label: r.label,
                                                                                    value: r.value
                                                                                }))}
                                                                                style={{width: "100%"}}
                                                                                $selectorStyles={{height: "100%"}}/>

                                                                {hasParameter && (
                                                                    <EnhancedTextField label={"Rule Parameter"}
                                                                                       disabled={!hasPermission}
                                                                                       value={
                                                                                           rule.Type === "match" || rule.Type === "notmatch"
                                                                                               ? rule.Parameters.regex
                                                                                               : rule.Parameters.value
                                                                                       }
                                                                                       onChange={(e: any) => {
                                                                                           if(rule.Type === "match" || rule.Type === "notmatch") {
                                                                                               updateValidationRule(index, {
                                                                                                   ...rule,
                                                                                                   Parameters: {
                                                                                                       regex: e.target.value
                                                                                                   }
                                                                                               })
                                                                                           }
                                                                                           else {
                                                                                                  updateValidationRule(index, {
                                                                                                    ...rule,
                                                                                                    Parameters: {
                                                                                                         value: e.target.value
                                                                                                    }
                                                                                                  })
                                                                                           }
                                                                                       }}
                                                                                       style={{width: "100%"}}/>
                                                                )}


                                                                <EnhancedTextField label={"Rule Note"} multiline
                                                                                   disabled={!hasPermission}
                                                                                   value={rule.Note}
                                                                                   onChange={(e: any) => updateValidationRule(index, {
                                                                                       ...rule,
                                                                                       Note: e.target.value
                                                                                   })}
                                                                                   style={{width: "100%"}}/>

                                                                <EnhancedTextField label={"Rule Error Message"}
                                                                                   disabled={!hasPermission}
                                                                                   multiline
                                                                                   value={rule.ErrorMessage}
                                                                                   onChange={(e: any) => updateValidationRule(index, {
                                                                                       ...rule,
                                                                                       ErrorMessage: e.target.value
                                                                                   })}
                                                                                   style={{width: "100%"}}/>

                                                                <Grid container justifyContent={"flex-end"}
                                                                      style={{gap: pageContentGap}}>
                                                                    <Button variant="outlined" disabled={!hasPermission} onClick={() => addCondition(index)}>
                                                                        Add Condition
                                                                    </Button>

                                                                    <Button variant="contained" color="error" disabled={!hasPermission} onClick={() => removeValidationRule(index)}>
                                                                        Remove Rule
                                                                    </Button>
                                                                </Grid>

                                                                <Grid container justifyContent={"flex-end"}>
                                                                    {rule.Conditions && rule.Conditions.map((condition: any, conditionIndex: number) => {
                                                                        return (
                                                                            <RuleConditonalInput ruleTypes={ruleTypes}
                                                                                                 rule={condition}
                                                                                                 hasPermission={hasPermission}
                                                                                                 validationErrors={validationErrors}
                                                                                                 ruleIndex={index}
                                                                                                 conditionalIndex={conditionIndex}
                                                                                                 updateValidationRuleCondition={updateValidationRuleCondition}
                                                                                                 removeCondition={removeCondition}
                                                                            />
                                                                        )
                                                                    })}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Grid>
                                )}

                                {hasPermission && (
                                    <Button variant="outlined" color="primary" onClick={() => addValidationRule()}>
                                        + Add Validation Rule
                                    </Button>
                                )}
                            </>
                        }/>
    );
}
